import React, { useContext } from 'react';

import { Grid, Chip, Stack } from '@mui/material';

import resistanceGenesReference from './resistanceGenesReference.js'
import { imputedFromAntibiotic } from './imputationUtils.js';
import { IdentifiedSpeciesFiltersContext } from '../context/IdentifiedSpeciesFiltersContext.js';

const CALL_COLORS = {
  default: '#0081ff',
  resistant: '#ff9800',
  susceptible: 'green',
  resistanceGene: 'grey'
};

const styles = {
  container: {
    pl: 3,
    pr: 1,
    textAlign: 'center',
    borderBottom: '#bdbdbd 2px solid',
    borderLeft: '#bdbdbd 2px solid'
  },
  drugName: {
    textAlign: 'left'
  },
  smallText: {
    fontWeight: 'normal'
  },
  confidenceText: {
    color: 'grey'
  },
  imputedText: {
    color: 'grey'
  },
  resistanceGenes: {
    justifyContent: 'left',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: 1
  }
};

function predictionIsSusceptible(predictionAttributes) {
  if (!predictionAttributes) {
    return false;
  }

  return predictionAttributes.amr_call === 'S' || predictionAttributes.call === 'S';
}

function predictionIsResistant(predictionAttributes) {
  if (!predictionAttributes) {
    return false;
  }

  return predictionAttributes.amr_call === 'R' || predictionAttributes.call === 'R';
}

export function isHighPerformingModel(predictionAttributes) {
  if (!predictionAttributes) {
    return false;
  }

  return ['H', 'VH'].includes(predictionAttributes.model_performance_label);
}

function isKgASTPrediction(predictionAttributes) {
  if (!predictionAttributes) {
    return false;
  }

  return !!predictionAttributes.model_performance_label;
}

function resistanceGeneIsRelevantForAntibiotic(resistanceGene, antibiotic) {
  if(!resistanceGenesReference[resistanceGene]) {
    return false;
  }

  return resistanceGenesReference[resistanceGene].antibiotics.includes(antibiotic);
}

function AMRPredictionRow(props) {
  const {
    prediction,
    amrPredictions,
    isDiagnosticView,
    resistanceGenes,
  } = props;

  const { showResistanceGenes, forceShowIAndRCalls } = useContext(IdentifiedSpeciesFiltersContext);

  let showSNS = process.env.REACT_APP_KEYNOME_MAKE_S_NS_CALLS && !forceShowIAndRCalls;
  let callColor = showSNS ? CALL_COLORS.resistant: CALL_COLORS.default;
  let callText = showSNS ? 'Non-Susceptible' : 'Intermediate';
  
  if (predictionIsResistant(prediction.attributes)) {
    callColor = CALL_COLORS.resistant;
    callText = showSNS ? 'Non-Susceptible' : 'Resistant';
  } else if (predictionIsSusceptible(prediction.attributes)) {
    callColor = CALL_COLORS.susceptible;
    callText = 'Susceptible';
  } else if (prediction.resistanceGenes) {
    callColor = CALL_COLORS.resistanceGene;
    callText = (
      <small style={styles.smallText}>
        Resistance Gene{prediction.resistanceGenes.length === 1 ? '' : 's'} Found
      </small>
    )
  }

  const predictionAttributes = prediction.attributes;

  let confidenceContent = null;
  if (!isDiagnosticView && isKgASTPrediction(predictionAttributes)) {
    const confidenceText = isHighPerformingModel(predictionAttributes) ? 'Qualified Model' : 'R&D Model';
    confidenceContent = (
      <Grid item xs={12} sm={3}>
        <p>
          <b><span style={styles.confidenceText}>{confidenceText}</span></b>
        </p>
      </Grid>
    );
  }

  let imputedFromContent = null;
  if (!isDiagnosticView && !isKgASTPrediction(predictionAttributes) && !prediction.resistanceGenes) {
    imputedFromContent = (
      <Grid item xs={12} sm={3}>
        <p>
          <span style={styles.imputedText}>
            <small>Imputed from: <b>{imputedFromAntibiotic(amrPredictions, predictionAttributes)}</b> prediction</small>
          </span>
        </p>
      </Grid>
    );
  }

  let resistanceGeneContent = null;
  if (!isDiagnosticView && resistanceGenes && showResistanceGenes && !prediction.resistanceGenes) {
    resistanceGeneContent = (
      <Stack direction="row" spacing={1} useFlexGap sx={styles.resistanceGenes}>
        {
          [...resistanceGenes]
            .filter(resistanceGene => resistanceGeneIsRelevantForAntibiotic(resistanceGene, predictionAttributes.model_drug || predictionAttributes.drug))
            .sort((a, b) => a.length - b.length)
            .map((item, index) => (
              <Chip key={index} size="small" label={resistanceGenesReference[item].display_name} color="primary" />
            ))
        }
      </Stack>
    );
  }

  if (!isDiagnosticView && prediction.resistanceGenes && showResistanceGenes) {
    resistanceGeneContent = (
      <Stack direction="row" spacing={1} useFlexGap sx={styles.resistanceGenes}>
        {
          prediction.resistanceGenes
            .sort((a, b) => a.length - b.length)
            .map((item, index) => (
              <Chip key={index} size="small" label={item} color="primary" />
            ))
        }
      </Stack>
    );
  }

  return (
    <Grid container sx={styles.container}>
      <Grid item xs={12} sm={isDiagnosticView ? 10 : 3} sx={styles.drugName}>
        <p><b>{prediction.attributes?.model_drug || prediction.attributes?.drug || prediction.drug}</b></p>
      </Grid>
      {prediction.resistanceGenes ? <Grid item xs={12} sm={2}></Grid> : null}
      <Grid item xs={12} sm={prediction.resistanceGenes ? 3 : 2}>
        <b><p style={{ color: callColor }}>{callText}</p></b>
      </Grid>
      {confidenceContent}
      {imputedFromContent}
      <Grid item xs={3}>
        {resistanceGeneContent}
      </Grid>
    </Grid>
  );
}

export default AMRPredictionRow;
